/**
 * PATH: packages/email-hub/src/feature/components/email-context/useJobSiblingEmails.ts
 * FILE_ID: EMAILHUB-EMAILCONTEXT-SIBLINGS-001
 * PURPOSE: One query for "other emails on this job", shared by rail and chip.
 * WHY: Lifted verbatim out of EmailJobContextRail so the tablet-width chip
 *      popover reads the SAME cache entry (['job-emails', jobId], the key
 *      JobDetailPage also seeds) instead of introducing a second request or a
 *      second sort order for the same list.
 * FLOW:
 * ┌──────────────┐   ┌───────────────────┐   ┌────────────────────────────┐
 * │ email.job_id  │ → │ /api/emails/?job  │ → │ siblings (self dropped,     │
 * │               │   │                   │   │ newest first) + isPending   │
 * └──────────────┘   └───────────────────┘   └────────────────────────────┘
 * DEPENDENCIES:
 * - @tanstack/react-query: shared cache with JobDetailPage's job-emails query.
 * - ../../../_internal/services/quotechApi: authenticated transport.
 * RELATED:
 * - ./JobSiblingEmailList.tsx: the only renderer of this data.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { quotechJson } from '../../../_internal/services/quotechApi'
import type { Email } from '../../hooks/useEmails'

export interface SiblingEmail {
  id: string
  subject: string
  from_name: string
  from_address: string
  received_at: string
  is_read: boolean
  has_attachments: boolean
  attachment_count: number
  category?: string
  synced_by_name?: string | null
  synced_by_email?: string | null
}

interface SiblingListResponse {
  results?: SiblingEmail[]
}

/**
 * Pure: raw payload → the list an operator should see.
 *
 * Drops the email being read (they know what they opened) and sorts newest
 * first so the conversation flow is obvious. Exported for its own test.
 */
export function selectJobSiblings(
  payload: SiblingListResponse | SiblingEmail[] | undefined,
  currentEmailId: string,
): SiblingEmail[] {
  const raw = Array.isArray(payload) ? payload : payload?.results ?? []
  return raw
    .filter((sibling) => sibling.id !== currentEmailId)
    .sort(
      (a, b) =>
        new Date(b.received_at).getTime() - new Date(a.received_at).getTime(),
    )
}

export interface JobSiblingEmails {
  /** null when the email is not linked to a job — there are no siblings. */
  jobId: number | null
  siblings: SiblingEmail[]
  isPending: boolean
}

export function useJobSiblingEmails(email: Email): JobSiblingEmails {
  const jobId = email.job_id ?? null

  const { data, isPending } = useQuery<SiblingListResponse | SiblingEmail[]>({
    queryKey: ['job-emails', jobId],
    queryFn: async () => {
      const { data: payload } = await quotechJson<
        SiblingListResponse | SiblingEmail[]
      >(`/api/emails/?job_id=${jobId}&limit=200`)
      return payload ?? []
    },
    enabled: jobId != null,
    staleTime: 60_000,
  })

  const siblings = useMemo(
    () => selectJobSiblings(data, email.id),
    [data, email.id],
  )

  return { jobId, siblings, isPending }
}
